import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { captureApiError } from '@core/sentry';
import { ApiError } from '@core/api/apiClient';

/**
 * Optional metadata a query or mutation can declare so global error reporting
 * produces well-contextualized Sentry events.
 *
 * - silent: skip reporting to Sentry under any condition.
 * - silentOn: status codes to skip (e.g. [401, 404, 410] for token-based link
 *   pages where those statuses mean "invalid/expired link" as UX, not a bug).
 *   5xx, timeouts and network errors still report.
 * - source: short low-cardinality label emitted as a tag and included in context.
 */
export interface AppQueryMeta extends Record<string, unknown> {
  silent?: boolean;
  silentOn?: number[];
  source?: string;
}

// Register lives in @tanstack/query-core; augmenting the re-export does not work.
declare module '@tanstack/query-core' {
  interface Register {
    queryMeta: AppQueryMeta;
    mutationMeta: AppQueryMeta;
  }
}

/**
 * Global error handler for queries and mutations. Called by the cache
 * `onError` hooks. Keep this the ONLY place in the app that reports
 * errors flowing through React Query to Sentry.
 *
 * Rule to avoid double-capture in Sentry:
 * - Errors from React Query queries/mutations are reported HERE (global cache).
 *   Do NOT call captureApiError/capturePaymentError again inside the component's
 *   onError or catch — opt out with `meta: { silent: true }` if you need full
 *   control at the callsite.
 * - Errors from direct api.post/api.get calls (outside React Query) must be
 *   caught at the caller and reported with captureApiError/capturePaymentError.
 */
function handleReactQueryError(
  error: unknown,
  meta: AppQueryMeta | undefined,
  kind: 'query' | 'mutation',
  keyPreview: string,
) {
  if (meta?.silent) return;
  if (meta?.silentOn && error instanceof ApiError && typeof error.status === 'number') {
    if (meta.silentOn.includes(error.status)) return;
  }
  captureApiError(error, {
    source: meta?.source ?? `react-query-${kind}`,
    reactQuery: {
      kind,
      key: keyPreview,
    },
  });
}

// Match strings that look like tokens: at least 24 chars, and containing
// either a char outside [A-Za-z] (digit or punctuation typical of tokens) or
// mixed-case with length >= 40. Plain camelCase identifiers like
// "guaranteeReservation" (20 chars) should not match.
const TOKEN_LIKE_RE = /^(?=.{24,})(?:[A-Za-z0-9_.+/=-]*[0-9_.+/=-][A-Za-z0-9_.+/=-]*|[A-Za-z]{40,})$/;

export function redactValue(value: unknown): unknown {
  if (typeof value === 'string' && TOKEN_LIKE_RE.test(value)) return '<redacted>';
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = redactValue(v);
    return out;
  }
  return value;
}

export function safeKeyPreview(key: unknown): string {
  if (key === undefined) return '<none>';
  try {
    return JSON.stringify(redactValue(key)).slice(0, 200);
  } catch {
    return String(key).slice(0, 200);
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleReactQueryError(error, query.meta, 'query', safeKeyPreview(query.queryKey));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _onMutateResult, mutation) => {
      const keyPreview =
        mutation.options.mutationKey !== undefined
          ? safeKeyPreview(mutation.options.mutationKey)
          : `mutationId:${mutation.mutationId}`;
      handleReactQueryError(error, mutation.meta, 'mutation', keyPreview);
    },
  }),
});
